import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { Button } from "../common";
import { useTranslation } from "../../i18n";
import { useLocalePath } from "../../hooks/useLocalePath";
import { PAGE_GUTTER_X } from "../../layout/pageShell";
import packageJson from "../../../package.json";

const Footer = () => {
  const { t } = useTranslation();
  const lp = useLocalePath();

  const quickLinks = [
    { label: t("nav.home"), href: lp("/") },
    { label: t("nav.explore"), href: lp("/explore") },
    { label: t("nav.pricing"), href: lp("/pricing") },
    { label: t("nav.about"), href: lp("/about") },
    { label: t("nav.blog"), href: lp("/blog") },
    { label: t("nav.contact"), href: lp("/contact") },
  ];

  const forBusinessLinks = [
    { label: t("nav.businessDashboard"), href: lp("/dashboard") },
    { label: t("nav.listYourBusiness"), href: lp("/auth/onboarding/business") },
    { label: t("nav.pricingPlans"), href: lp("/pricing") },
    { label: t("nav.resources"), href: lp("/about") },
    { label: t("nav.helpCenter"), href: lp("/contact") },
  ];

  return (
    <>
      {/* Ready to grow your business — Figma CTA */}
      <section className="relative flex flex-col items-center justify-center gap-[34px] overflow-hidden bg-cta-gradient lg:py-[96px] py-16 z-10">
        <div className="pointer-events-none absolute inset-0 opacity-25" aria-hidden>
          <div className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-white/40 blur-3xl" />
          <div className="absolute -bottom-32 -right-32 h-[420px] w-[420px] rounded-full bg-white/40 blur-3xl" />
        </div>

        <div className={`relative w-full max-w-page mx-auto ${PAGE_GUTTER_X} text-center`}>
          <h2 className="text-white lg:text-[52px] text-[40px] font-bold">
            {t("footer.readyToGrow")}
          </h2>
          <p className="text-[#FFFFFFCC] text-lg">
            {t("footer.readySubtext")}
          </p>

          <div className="flex lg:flex-row flex-col items-center justify-center gap-5 mt-12">
            <Button
              variant="secondary"
              label={t("footer.getStartedFree")}
              icon="mdi:chevron-right"
              iconDirection="right"
              className="rounded-[15px]"
              to={lp("/register")}
            />
            <Button
              variant="transparent-white"
              label={t("footer.contactSales")}
              className="rounded-[15px]"
              to={lp("/contact")}
            />
          </div>
        </div>
      </section>

      <footer className="bg-[#010E1F] pt-[43px]">
        <div className={`w-full max-w-page mx-auto ${PAGE_GUTTER_X}`}>
          <div className="flex lg:flex-row flex-col lg:items-start items-center lg:justify-between">
            <div className="lg:w-1/4 gap-5 flex flex-col items-center justify-center">
              <img
                src="/icons/logo_dark.svg"
                alt="logo-dark"
                className="w-[180px] h-auto object-cover"
              />
              <p className="text-center text-[#A2AED2]">
                {t("footer.tagline")}
              </p>
            </div>

            {/* Links group */}
            <div className="lg:w-auto w-full flex lg:flex-row flex-col items-start lg:gap-[120px] gap-[50px]">
              <div className="text-[#A2AED2]">
                <span className="text-xl font-semibold">{t("footer.quickLinks")}</span>
                <ul className="list-none flex flex-col items-start gap-[7px] mt-5">
                  {quickLinks.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.href}
                        className="hover:text-primary transition-colors duration-300"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="text-[#A2AED2]">
                <span className="text-xl font-semibold">{t("footer.forBusinesses")}</span>
                <ul className="list-none flex flex-col items-start gap-[7px] mt-5">
                  {forBusinessLinks.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.href}
                        className="hover:text-primary transition-colors duration-300"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="text-[#A2AED2]">
                <span className="text-xl font-semibold">{t("footer.contact")}</span>
                <ul className="list-none flex flex-col items-start gap-5 mt-5">
                  <li className="flex flex-row items-center gap-2">
                    <Icon icon="mdi:email-outline" className="w-5 h-5" />
                    <span className="">support@prombiz.co.il</span>
                  </li>

                  <li className="flex flex-row items-center gap-2">
                    <Icon icon="mdi:map-marker-outline" className="w-5 h-5" />
                    <span className="">123 Business St. Tel Aviv, Israel</span>
                  </li>

                  <li>
                    <p>Sunday-Thursday</p>
                    <p>9:00-18:00 IST</p>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="py-[30px] border-t border-[rgba(162,_174,_210,_0.30)] flex lg:flex-row flex-col lg:items-center items-start lg:justify-between text-[#A2AED2] lg:mt-0 mt-[50px]">
            <div className="flex lg:flex-row lg:items-center flex-col items-start lg:gap-[30px] gap-[15px]">
              <Link to={lp("/terms")} className="hover:text-primary transition-colors duration-300">
                {t("nav.terms")}
              </Link>
              <Link to={lp("/privacy")} className="hover:text-primary transition-colors duration-300">
                {t("nav.privacy")}
              </Link>
              <Link to={lp("/privacy")} className="hover:text-primary transition-colors duration-300">
                Cookie Policy
              </Link>
            </div>

            <span className="lg:mt-0 mt-[15px] flex items-center gap-2">
              © {new Date().getFullYear()} PromBiz. {t("footer.allRightsReserved")}
              <span className="text-[#A2AED2]/70 text-sm" title="Build identifier">
                Build {process.env.REACT_APP_BUILD_LABEL || packageJson.version}
              </span>
            </span>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
